import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { useCenterPatients, useCourseLabels, useRenameCenterCouple, useReplaceCouplePartner } from "../../../center";
import type { CenterCouple } from "../../../center";
import { STUDENT_ERRORS } from "../refusals";

interface Props {
    centerId: string;
    couple: CenterCouple;
    therapistName: string;
    onClose: () => void;
}

/**
 * Editing one of the centre's couples: its name and, before it has met, who
 * is in it.
 *
 * A member's portal shows the sessions of the couple they are in now, so once
 * the couple has met its partners are its history: they are shown, not
 * changed, and another couple is a new one. Another therapist is a handover,
 * done from the list with "Reasignar".
 */
export default function CenterCoupleEditModal({ centerId, couple, therapistName, onClose }: Props) {
    const { t } = useTranslation();
    const rename = useRenameCenterCouple(centerId);
    const replace = useReplaceCouplePartner(centerId);
    const hasMet = (couple.session_count ?? 0) > 0;
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    const { patients } = useCenterPatients(hasMet ? undefined : centerId, {
        therapist_id: couple.therapist_id,
        limit: 500,
    });
    const partnerIds = new Set(couple.members.map((member) => member.patient_id));
    const candidates = patients
        .filter((patient) => !patient.is_anonymous && !partnerIds.has(patient.patient_id))
        .map((patient) => ({
            value: patient.patient_id,
            label: labelOf(patient),
            subLabel: patient.contact_email ?? undefined,
        }));

    const replacePartner = async (oldPatientId: string, newPatientId: string) => {
        setError(null);
        setSaved(false);
        try {
            await replace.mutateAsync({ coupleId: couple.couple_id, oldPatientId, newPatientId });
        } catch (err: unknown) {
            const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
            setError(t(STUDENT_ERRORS[code ?? ""] ?? "center.groups.errorGeneric"));
        }
    };
    const [name, setName] = useState(couple.display_name ?? "");
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setName(couple.display_name ?? "");
    }, [couple.display_name]);

    const save = async () => {
        setError(null);
        setSaved(false);
        try {
            await rename.mutateAsync({ coupleId: couple.couple_id, displayName: name.trim() || null });
            setSaved(true);
        } catch {
            setError(t("center.groups.errorGeneric"));
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            ariaLabel={t("center.couples.editTitle")}
            className="mx-4 w-full max-w-lg rounded-2xl bg-surface-raised p-6"
        >
            <form
                className="flex flex-col gap-5"
                onSubmit={(e) => {
                    e.preventDefault();
                    void save();
                }}
            >
                <div className="pr-14">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                        {t("center.couples.editTitle")}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-content-primary">
                        {couple.members.map((m) => `${m.first_name} ${m.last_name}`).join(" & ")}
                    </h3>
                    <p className="mt-1 text-sm text-content-tertiary">
                        {t("center.groups.mentorIs", { name: therapistName })}
                    </p>
                </div>

                <div>
                    <Label htmlFor="couple-edit-name">{t("center.students.coupleNameOptional")}</Label>
                    <Input
                        id="couple-edit-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("center.students.coupleNamePlaceholder")}
                    />
                </div>
                <fieldset className="flex flex-col gap-3">
                    <legend className="mb-1 text-sm font-semibold text-content-primary">
                        {t("center.couples.partners")}
                    </legend>
                    {couple.members.map((member, index) => (
                        <div
                            key={member.patient_id}
                            className="grid gap-2 rounded-xl border border-gray-200 p-3 dark:border-gray-800 sm:grid-cols-[1fr_1fr] sm:items-center"
                        >
                            <span className="text-sm font-medium text-content-primary">
                                {`${member.first_name} ${member.last_name}`}
                            </span>
                            {!hasMet && (
                                <div className="min-w-0">
                                    <FilterSelect
                                        options={candidates}
                                        value=""
                                        onChange={(picked) =>
                                            picked && void replacePartner(member.patient_id, picked as string)
                                        }
                                        placeholder={t("center.couples.replaceWith", { n: index + 1 })}
                                        searchPlaceholder={t("patient.searchPlaceholder")}
                                        className="border-border-strong bg-transparent text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </fieldset>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t(hasMet ? "center.couples.lockedHint" : "center.couples.partnersHint")}
                </p>

                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}
                {saved && (
                    <p role="status" className="text-sm text-brand-700 dark:text-brand-300">
                        {t("center.groups.saved")}
                    </p>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>
                        {t("common.close")}
                    </Button>
                    <Button
                        type="submit"
                        loading={rename.isPending}
                        disabled={rename.isPending || name.trim() === (couple.display_name ?? "")}
                    >
                        {t("common.save")}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
